import { Box, CircularProgress } from "@mui/material";
import { Select, Typography } from "@powerdiary/components-library/lib";
import {
    useClientPortalSelector,
    useClientPortalDispatch,
    selectBooking,
    setService,
    setServiceCategory,
} from "../../../../redux";
import { IServiceData } from "../../bookingSlice.types";
import ListItem from "../ListItem";
import styles from "./Service.module.css";
import { useServices } from "../../hooks/useServices";
import { SaleableCategoryModel, ServiceModel } from "types/generated/models";
import { useAnalytics } from "hooks/useAnalytics";
import { useMemo } from "react";
import { useBookingNavigation } from "../../hooks/useBookingNavigation";

export default function Service() {
    const { service, session } = useClientPortalSelector(selectBooking);
    const dispatch = useClientPortalDispatch();
    const { services, saleableCategories, isLoading } = useServices();
    const { track } = useAnalytics();
    const { handleNext } = useBookingNavigation();

    const selectedServiceId = service.data?.serviceId || null;
    const selectedCategory = service.selectedCategory || "";

    const handleServiceSelect = (selectedService: ServiceModel) => {
        const serviceData: IServiceData = {
            serviceId: selectedService.id || 0,
            serviceName: selectedService.name || "",
            portalDeposit: selectedService.portalDeposit,
            amount: selectedService.amount,
            taxRate: selectedService.taxRate,
        };

        dispatch(setService(serviceData));
        track("booking_step_service_completed", {
            booking_type: session.bookingType,
            service_id: selectedService.id,
            service_name: selectedService.name,
            service_duration: selectedService.duration,
        });
        handleNext();
    };

    const handleCategoryChange = (categoryId: string) => {
        dispatch(setServiceCategory(categoryId));
    };

    const filterOptions = useMemo(
        () =>
            saleableCategories
                ? saleableCategories.map((category: SaleableCategoryModel) => ({
                      value: category.value && category.value !== "" ? category.value : "all",
                      label: category.label || "",
                  }))
                : [],
        [saleableCategories]
    );

    const normalizedCategory = selectedCategory && selectedCategory !== "all" ? Number(selectedCategory) : null;

    const filteredServices = useMemo(() => {
        if (!services) return [] as ServiceModel[];
        if (normalizedCategory == null || Number.isNaN(normalizedCategory)) return services;
        return services.filter((s) => s.saleableCategoryId === normalizedCategory);
    }, [services, normalizedCategory]);

    const showFilter = filterOptions.length > 1;

    return (
        <Box className={styles.wrapper} data-test-id="section_services">
            <Typography mb={1} variant="heading4">
                Select a service
            </Typography>

            {showFilter && !isLoading && (
                <Box mb={2} display="flex" alignItems="center" gap={2}>
                    <Typography variant="paragraphMediumSemiBold">Filter by category:</Typography>
                    {(() => {
                        const hasAll = filterOptions.some((o) => o.value === "all");
                        const isSelectedValid =
                            !!selectedCategory && filterOptions.some((o) => o.value === selectedCategory);
                        let selectValue: string;
                        if (hasAll) {
                            selectValue = selectedCategory || "all";
                        } else if (isSelectedValid) {
                            selectValue = selectedCategory;
                        } else {
                            selectValue = filterOptions[0]?.value ?? "";
                        }
                        return (
                            <Select
                                options={filterOptions}
                                value={selectValue}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    handleCategoryChange(e.target.value)
                                }
                                dataTestIdSuffix="service_category"
                                size="small"
                                sx={{ minWidth: 200 }}
                                inputProps={{ "data-test-id": "input_service_category" }}
                            />
                        );
                    })()}
                </Box>
            )}

            {isLoading ? (
                <Box display="flex" justifyContent="center" p={4} data-test-id="loading_indicator_services">
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {filteredServices && filteredServices.length > 0 ? (
                        <Box className={styles.listWrapper}>
                            {filteredServices.map((serviceItem) => (
                                <ListItem
                                    key={serviceItem.id}
                                    title={serviceItem.name || ""}
                                    description={serviceItem.description || ""}
                                    price={serviceItem.amount}
                                    duration={`${serviceItem.duration} Minutes`}
                                    selected={selectedServiceId === serviceItem.id}
                                    onClick={() => handleServiceSelect(serviceItem)}
                                    dataTestIdSuffix={`service_${serviceItem.id}`}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Typography
                            variant="paragraphMediumSemiBold"
                            textAlign="center"
                            data-test-id="text_no_services"
                        >
                            {selectedCategory
                                ? "No services are available in this category."
                                : "No services are available."}
                        </Typography>
                    )}
                </>
            )}
        </Box>
    );
}
